import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import { Edit, Trash2, Building2, CheckCircle, Calendar, XCircle } from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import apiCompany from "../api";
import apiShared from "modules/swiftboard/api";
import StatusFilter from "modules/swiftboard/components/statusFilter";
import { loadDependencies } from "./features/dependencies";
import { fieldsConfig } from "./features/fields";
import { validation } from "./features/validation";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";

const CompanyConfiguration = ({ onTotalCountChange }) => {
    const { showAlert } = useAlert();
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [resetTrigger, setResetTrigger] = useState(0);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [confirmData, setConfirmData] = useState({
        isOpen: false,
        title: "",
        message: "",
        confirmText: "Yes",
        cancelText: "No",
        icon: null,
        resolve: null,
    });

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });

    const fetchDatas = async () => {
        setLoading(true);
        try {
            const payload = {
                searchText,
                skip: (currentPage - 1) * pageSize,
                limit: pageSize,
                ...(statusFilter !== "ALL" && {
                    is_activated: statusFilter === "ACTIVE"
                })
            };

            const response = await apiShared.getAllCompanies(payload);

            if (response.data.status) {
                setData(response.data.data);
                setTotalCount(response.data.total_count || 0);
                onTotalCountChange?.(response.data.total_count || 0);
            }
            else {
                showAlert({ type: 'warning', message: response.data?.message });
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        }
        finally {
            setLoading(false);
        }
    }

    const openConfirm = (options) => {
        return new Promise((resolve) => {
            setConfirmData({
                ...options,
                isOpen: true,
                resolve,
            });
        });
    };

    const handleConfirmClose = (result) => {
        if (confirmData.resolve) {
            confirmData.resolve(result);
        }

        setConfirmData((prev) => ({
            ...prev,
            isOpen: false,
            resolve: null,
        }));
    };


    const handleDeleteSelected = async (ids = []) => {

        if (ids.length === 0) {
            showAlert({ type: 'warning', message: "Please select at least one company to delete." });
            return;
        }
        const confirmed = await openConfirm({
            title: "Delete Company",
            message:
                ids.length > 1
                    ? `Are you sure you want to delete ${ids.length} selected Companies? This action cannot be undone.`
                    : "Are you sure you want to delete this Company? This action cannot be undone.",
            confirmText: "Delete",
            cancelText: "Cancel",
            icon: <Trash2 size={24} className="text-danger" />,
        });

        if (!confirmed) return;
        try {
            const response = await apiCompany.delete(ids)

            if (response.data.status) {
                showAlert({ type: 'success', message: response.data?.message });

                fetchDatas();
                setSelectedRows([]);
            }
            else {
                showAlert({ type: 'warning', message: response.data?.message });
            }
        }
        catch (error) {
            if (error.response) {
                showAlert({ type: 'error', message: error.response?.data?.message });
            } else {
                showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
            }
        }
    };

    useEffect(() => {
        fetchDatas();
    }, [currentPage, pageSize, searchText, statusFilter]);

    useEffect(() => {
        const sorted = sortData(data, sortConfig);
        setFilteredData(sorted);
    }, [data, sortConfig]);


    const handleSearch = (value) => {
        setSearchText(value)
        setCurrentPage(1); // reset to first page
    };

    // Handle page change
    const handlePageChange = (page, newPageSize = pageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
        }
    };



    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handleCreate = () => {
        setSelectedClient(null); // Clear selected client for create mode
        setIsModalOpen(true);
    };
    const handleEdit = (id) => {
        const clientToEdit = data.find(client => client.id === id);
        if (clientToEdit) {
            setSelectedClient(clientToEdit);
            setIsModalOpen(true);
        }
    };

    const handleToggleStatus = async (row) => {
        const confirmed = await openConfirm({
            title: `${row.is_activated ? "Deactivate" : "Activate"} Company`,
            message: `Are you sure you want to ${row.is_activated ? "deactivate" : "activate"} this Company?`,
            confirmText: "Save Changes",
            cancelText: "Cancel",
            icon: <CheckCircle size={24} className="text-success" />,
        });

        if (!confirmed) return;
        try {
            const response = await apiCompany.toggleStatus(row.id);

            if (response.data.status) {
                showAlert({
                    type: "success",
                    message: `User ${row.is_activated ? "deactivated" : "activated"} successfully`
                });
                fetchDatas();
            } else {
                showAlert({ type: "warning", message: response.data?.message });
            }
        } catch (error) {
            showAlert({
                type: "error",
                message: error.response?.data?.message || "Something went wrong"
            });
        }
    };


    const handleRefresh = () => {
        setSearchText('');// to reset search text  
        setCurrentPage(1);// to reset page number to 1
        setResetTrigger(prev => prev + 1);// to reset search in the search bar 
        fetchDatas();
    };

    const handleModalSuccess = () => {
        fetchDatas(); // Refresh the list
        setSelectedRows([]); // Clear selections
    };

    const columns = [
        {
            key: "id",
            label: "Company ID",
            type: "number",
            width: "100px",
            sortable: true,
            render: (row) => (
                <div className="font-mono text-xs font-medium text-text_primary">
                    {`#COMP${String(row.id).padStart(3, "0")}`}
                </div>
            ),
        },
        {
            key: "company_name",
            label: "Company",
            type: "string",
            width: "250px",
            sortable: true,
            render: (row) => (
                <div className="flex items-center gap-3">
                    {row.company_url ? (
                        <img
                            src={row.company_url}
                            alt={row.company_name}
                            className="w-10 h-10 rounded-md object-contain border bg-surface p-1 shadow-sm"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-md bg-surface_alt border flex items-center justify-center">
                            <Building2 size={20} className="text-text_muted" />
                        </div>
                    )}

                    <div className="font-medium text-text_primary">
                        {row.company_name}
                    </div>
                </div>
            ),
        },
        {
            key: "owner",
            label: "Company Manager",
            type: "string",
            width: "120px",
            sortable: false,
            render: (row) => (
                <div
                    title={`Role: ${row.owner?.user_role?.role || "N/A"}`}
                    className="cursor-default"
                >
                    <div className="font-medium text-text_primary">
                        {row.owner?.first_name} {row.owner?.last_name}
                    </div>
                    <div className="text-xs text-text_muted">
                        {row.owner?.email}
                    </div>
                </div>
            ),
        },
        {
            key: "country",
            label: "Country",
            type: "string",
            width: "180px",
            sortable: false,
            render: (row) => (
                <div
                    title={`Address: ${row.company_address || "N/A"}, ${row.city || "N/A"}, ${row.country?.country_name || "N/A"}`}
                    className="text-sm text-text_primary cursor-default"
                >
                    {row.country?.country_name || "N/A"}
                </div>
            ),
        },
        {
            key: "_count.users",
            label: "Users",
            type: "number",
            width: "10px",
            sortable: false,
            render: (row) => (
                <div className="font-medium text-text_primary">
                    {row._count?.users || 0}
                </div>
            ),
        },
        {
            key: "_count.projects",
            label: "Projects",
            type: "number",
            width: "10px",
            sortable: false,
            render: (row) => (
                <div className="font-medium text-text_primary">
                    {row._count?.projects || 0}
                </div>
            ),
        },
        {
            key: "is_activated",
            label: "Status",
            type: "boolean",
            width: "130px",
            sortable: true,
            render: (row) => (
                <span
                    className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded border ${row.is_activated
                        ? "bg-success_bg text-success border-success_border"
                        : "bg-danger_bg text-danger border-danger_border"
                        }`}
                >
                    {row.is_activated ? "Active" : "Inactive"}
                </span>
            ),
        },

        {
            key: "created_date",
            label: "Created Date",
            width: "140px",
            render: (row) => (
                <div className="flex items-center gap-1.5 text-xs text-text_primary">
                    <Calendar size={12} className="text-text_muted" />
                    <LocalDateTime utcTime={row.created_date} type="datetime" />
                </div>
            ),
            sortable: true,
        },
        {
            key: "updated_date",
            label: "Last Update",
            width: "140px",
            render: (row) => (
                <div className="flex items-center gap-1.5 text-xs text-text_primary">
                    <Calendar size={12} className="text-text_muted" />
                    <LocalDateTime utcTime={row.updated_date} type="datetime" />
                </div>
            ),
            sortable: true,
        },
    ];
    // Custom action buttons for each row
    const renderRowActions = (row) => (
        <div className="flex items-center gap-1.5">

            {/* Edit */}
            <button
                className="p-1.5 rounded-lg bg-info_bg text-info hover:bg-info_bg transition-all duration-200"
                onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(row.id);
                }}
                title="Edit Employee"
            >
                <Edit size={16}  />
            </button>



            {/* Delete */}
            <button
                className="p-1.5 rounded-lg bg-danger_bg text-danger hover:bg-danger_bg transition-all duration-200"
                onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSelected([row.id]);
                }}
                title="Delete Employee"
            >
                <Trash2 size={16}  />
            </button>

            {/* Activate / Deactivate */}
             <button
        className={`p-1.5 rounded-lg transition-all duration-200 ${row.is_activated
          ? "bg-danger_bg text-danger hover:bg-danger_bg"
          : "bg-success_bg text-success hover:bg-success_bg"
          }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Active - Click to Deactivate" : "Inactive - Click to Activate"}
      >
        {row.is_activated ? (
          <XCircle size={16}  />
        ) : (
          <CheckCircle size={16}   />
        )}
      </button>
        </div>
    );

    return (
        <div className="h-full flex flex-col overflow-hidden bg-surface_alt">

            <div className="flex flex-col h-full gap-4">


                <StatusFilter
                    label="Filter by status:"
                    options={[
                        "ALL",
                        "ACTIVE",
                        "INACTIVE",
                    ]}
                    selected={statusFilter}
                    onChange={(status) => {
                        setStatusFilter(status);
                        setCurrentPage(1);
                    }}
                />
                <TableToolBar
                    onSearch={handleSearch}
                    onRefresh={handleRefresh}
                    onCreate={handleCreate}
                    onDeleteSelected={() => handleDeleteSelected(selectedRows)}
                    selectedCount={selectedRows.length}
                    resetSearchTrigger={resetTrigger}
                />

                {data?.length === 0 && !loading ? (<div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
                    <NoDataState
                        icon="alert"
                        title="No Company Found"
                        message="No company data is available! Please add a company to get started."
                        showRefresh={false}
                        showBack={false}
                        showCreate={true}
                        createText="Create New Company Configuration"
                        onCreate={handleCreate}
                        size="medium"
                    />
                </div>) : (<>

                    <div className="flex-1 overflow-hidden border rounded-lg border-border">
                        <TableGrid
                            data={filteredData}
                            totalCount={totalCount}
                            loading={loading}
                            currentPage={currentPage}
                            columns={columns}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onSelectionChange={setSelectedRows}
                            pageSize={pageSize}
                            renderActions={renderRowActions}
                        />
                    </div>

                    <Pagination
                        totalCount={totalCount}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />

                    {confirmData.isOpen && (
                        <ConfirmDialogue
                            confirmData={confirmData}
                            handleConfirmClose={handleConfirmClose}
                        />
                    )}


                </>)}
            </div>


            {/* Add the modal */}
            {/* <FormModel
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                id={selectedClient?.id}
                onSuccess={handleModalSuccess}
            /> */}

            <EntityFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                id={selectedClient?.id}
                title="Company"
                fetchById={apiCompany.getById}
                createFn={apiCompany.create}
                updateFn={apiCompany.update}
                dependenciesLoader={loadDependencies}
                fields={fieldsConfig}
                validationSchema={validation}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default CompanyConfiguration;